/* eslint-disable react/prop-types */
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, History as HistoryIcon, Replay as ReplayIcon } from '@mui/icons-material';
import { MaterialReactTable, MRT_ColumnDef, MRT_Row, useMaterialReactTable } from 'material-react-table';
import { ScheduleGraphRowDTO } from '../../../magic-report/magic-report.type';
import { ProjectPreviousFundingDTO } from '../../../../project/project.type';
import { useMRTStyling } from '../../../../../hooks/useMRTStyling';
import WhiteFab from '../../../../common/WhiteFab';
import DynamicDatePicker from '../../../../common/DynamicDatePicker';
import { formatCurrency } from 'common/utils/common.util';

interface ScheduleGraphTableProps {
  rows: ScheduleGraphRowDTO[];
  revisedBudget?: number;
  onChange: (rows: ScheduleGraphRowDTO[]) => void;
  onHoveredRowId?: (id: string | null) => void;
  commencementDate?: string;
  // Inputs for "Repopulate from Previous Fundings"; button is hidden if omitted.
  previousFundings?: ProjectPreviousFundingDTO[];
  currentRetainagePercent?: number;
  currentContractSumToDate?: number;
}

// Rebuilds rows from Previous Fundings: % Complete is a running total, oldest first —
// contribution = (amountFunded / (1 - retainage%)) / contractSumToDate * 100, added to the prior row's %.
const buildScheduleGraphFromPreviousFundings = (
  previousFundings: ProjectPreviousFundingDTO[],
  currentRetainagePercent: number | undefined,
  currentContractSumToDate: number | undefined,
): ScheduleGraphRowDTO[] => {
  const retainageFraction = (currentRetainagePercent ?? 0) / 100;
  const canCompute = currentContractSumToDate != null && currentContractSumToDate > 0 && retainageFraction < 1;

  const sorted = [...previousFundings].sort((a, b) => {
    const da = a.applicationDate ?? '';
    const db = b.applicationDate ?? '';
    if (da && db) return da.localeCompare(db);
    if (da) return -1;
    if (db) return 1;
    return (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
  });

  let runningPercentComplete: number | undefined;
  return sorted.map(pf => {
    const contribution =
      canCompute && pf.amountFunded != null ? (pf.amountFunded / (1 - retainageFraction) / currentContractSumToDate!) * 100 : undefined;
    runningPercentComplete = contribution != null ? contribution + (runningPercentComplete ?? 0) : runningPercentComplete;

    return {
      id: crypto.randomUUID(),
      applicationNumber: pf.applicationNumber,
      applicationDate: pf.applicationDate,
      amountFunded: pf.amountFunded,
      percentComplete: runningPercentComplete,
      comment: pf.comment,
      isLastMonth: false,
    };
  });
};

type NewRowFormState = {
  applicationNumber: string;
  applicationDate: string | null;
  percentComplete: string;
  comment: string;
};

const createEmptyForm = (): NewRowFormState => ({
  applicationNumber: '',
  applicationDate: null,
  percentComplete: '',
  comment: '',
});

export const sortRows = (rows: ScheduleGraphRowDTO[]): ScheduleGraphRowDTO[] =>
  [...rows].sort((a, b) => {
    const dateA = a.applicationDate ?? '';
    const dateB = b.applicationDate ?? '';
    if (dateA && dateB) {
      const byDate = dateA.localeCompare(dateB);
      if (byDate !== 0) return byDate;
    } else if (dateA) {
      return -1;
    } else if (dateB) {
      return 1;
    }
    // Same date (or both undated) — tiebreak by % Complete ascending.
    const pctA = a.percentComplete ?? -Infinity;
    const pctB = b.percentComplete ?? -Infinity;
    return pctA - pctB;
  });

const calcPct = (cumulativeAmount: number | undefined, revisedBudget: number | undefined): number | undefined => {
  if (cumulativeAmount == null || !revisedBudget || revisedBudget <= 0) return undefined;
  return (cumulativeAmount / revisedBudget) * 100;
};

const recalcPercentages = (rows: ScheduleGraphRowDTO[], revisedBudget: number | undefined): ScheduleGraphRowDTO[] => {
  let running = 0;
  return rows.map(r => {
    if (r.amountFunded != null) running += r.amountFunded;
    // Cover-sheet rows already have percentComplete and no amountFunded — keep as-is.
    if (r.amountFunded == null && r.percentComplete != null) return r;
    return { ...r, percentComplete: calcPct(r.amountFunded != null ? running : undefined, revisedBudget) };
  });
};

const DatePickerCell: React.FC<{
  row: MRT_Row<ScheduleGraphRowDTO>;
  table: ReturnType<typeof useMaterialReactTable<ScheduleGraphRowDTO>>;
  updateRow: (id: string, patch: Partial<ScheduleGraphRowDTO>) => void;
}> = ({ row, table, updateRow }) => {
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      const inPopper = Array.from(document.querySelectorAll('.MuiPickersPopper-root')).some(el => el.contains(target));
      if (inPopper) return;
      table.setEditingCell(null);
    };
    document.addEventListener('mousedown', handleMouseDown, true);
    return () => document.removeEventListener('mousedown', handleMouseDown, true);
  }, [table]);

  return (
    <div ref={containerRef}>
      <DynamicDatePicker
        fieldName='applicationDate'
        value={row.original.applicationDate ?? null}
        onChange={(_field, val) => {
          updateRow(row.original.id, { applicationDate: val ?? undefined });
          table.setEditingCell(null);
        }}
        textFieldProps={{ size: 'small', placeholder: 'mm/dd/yyyy' }}
      />
    </div>
  );
};

// The commencement row is a display-only concern, derived fresh from `commencementDate` on every
// render — it's never part of `tableData`/`rows` and never sent through `onChange`, so it can't be
// duplicated by a save/reload round trip. No "Current" row anymore — that now comes from the
// backend's Cover-sheet extraction.
const buildCommencementRow = (commencementDate: string): ScheduleGraphRowDTO => ({
  id: 'commencement-row',
  applicationNumber: '0',
  applicationDate: commencementDate,
  amountFunded: undefined,
  percentComplete: 0,
  comment: 'Commencement',
  isLastMonth: false,
  isCommencement: true,
});

const ScheduleGraphTable: React.FC<ScheduleGraphTableProps> = ({
  rows,
  revisedBudget,
  onChange,
  onHoveredRowId,
  commencementDate,
  previousFundings,
  currentRetainagePercent,
  currentContractSumToDate,
}) => {
  const theme = useTheme();
  const [isRepopulateConfirmOpen, setIsRepopulateConfirmOpen] = useState(false);
  // `rows` may still contain a legacy persisted commencement row — strip it so it's never
  // double-counted against the freshly-computed one below.
  const stripLegacyCommencementRows = (input: ScheduleGraphRowDTO[]) => input.filter(r => !r.isCommencement);
  const [tableData, setTableData] = useState<ScheduleGraphRowDTO[]>(() =>
    recalcPercentages(sortRows(stripLegacyCommencementRows(rows)), revisedBudget),
  );
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newRowForm, setNewRowForm] = useState<NewRowFormState>(createEmptyForm);
  const { commonMRTStyling, getActionsColumnStyling } = useMRTStyling<ScheduleGraphRowDTO>();
  const actionsColumnStyling = getActionsColumnStyling();

  // Debounced hover — prevents flicker when the cursor moves between cells within the same row
  const hoverTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoveredIdRef = React.useRef<string | null>(null);
  const handleRowEnter = React.useCallback(
    (id: string) => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      if (hoveredIdRef.current !== id) {
        hoveredIdRef.current = id;
        onHoveredRowId?.(id);
      }
    },
    [onHoveredRowId],
  );
  const handleRowLeave = React.useCallback(() => {
    hoverTimeoutRef.current = setTimeout(() => {
      hoveredIdRef.current = null;
      onHoveredRowId?.(null);
    }, 80);
  }, [onHoveredRowId]);

  // Sync inbound rows when they differ from local state (by ID set + revisedBudget).
  // Never calls onChange — effects must not push data back up.
  const prevRevisedBudgetRef = React.useRef(revisedBudget);
  useEffect(() => {
    const budgetChanged = prevRevisedBudgetRef.current !== revisedBudget;
    prevRevisedBudgetRef.current = revisedBudget;

    setTableData(prev => {
      const incomingIds = rows.map(r => r.id).join(',');
      const prevIds = prev.map(r => r.id).join(',');
      const idsChanged = incomingIds !== prevIds;

      if (!idsChanged && !budgetChanged) return prev;

      // Re-sort and recalculate cumulative percentages whenever rows or budget change.
      const base = idsChanged ? stripLegacyCommencementRows(rows) : prev;
      return recalcPercentages(sortRows(base), revisedBudget);
    });
  }, [rows, revisedBudget]);

  const updateRow = useCallback(
    (id: string, patch: Partial<ScheduleGraphRowDTO>) => {
      const updated: ScheduleGraphRowDTO[] = [];
      setTableData(prev => {
        const mapped = prev.map(r => (r.id !== id ? r : { ...r, ...patch }));
        // Re-sort on date change so the row moves to its correct position immediately.
        const sorted = 'applicationDate' in patch ? sortRows(mapped) : mapped;
        const next = recalcPercentages(sorted, revisedBudget);
        updated.push(...next);
        return next;
      });
      updatedRowsRef.current = updated;
    },
    [revisedBudget],
  );

  // Flush pending onChange calls after render to stay outside setState updaters.
  const updatedRowsRef = React.useRef<ScheduleGraphRowDTO[] | null>(null);
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    if (updatedRowsRef.current !== null) {
      onChangeRef.current(updatedRowsRef.current);
      updatedRowsRef.current = null;
    }
  });

  const handleDelete = useCallback((row: MRT_Row<ScheduleGraphRowDTO>) => {
    // The commencement row is display-only — guard anyway in case the disabled delete button is bypassed.
    if (row.original.isCommencement) return;
    setTableData(prev => {
      const updated = prev.filter(r => r.id !== row.original.id);
      // Schedule onChange outside the updater via ref flush.
      updatedRowsRef.current = updated;
      return updated;
    });
  }, []);

  // Replaces every row — gated behind a confirmation dialog.
  const handleRepopulateFromPreviousFundings = useCallback(() => {
    const rebuilt = buildScheduleGraphFromPreviousFundings(previousFundings ?? [], currentRetainagePercent, currentContractSumToDate);
    setTableData(() => {
      updatedRowsRef.current = rebuilt;
      return rebuilt;
    });
    setIsRepopulateConfirmOpen(false);
  }, [previousFundings, currentRetainagePercent, currentContractSumToDate]);

  const handleConfirmAdd = () => {
    const percentComplete = newRowForm.percentComplete !== '' ? parseFloat(newRowForm.percentComplete) : undefined;
    const newRow: ScheduleGraphRowDTO = {
      id: crypto.randomUUID(),
      applicationNumber: newRowForm.applicationNumber.trim() || undefined,
      applicationDate: newRowForm.applicationDate || undefined,
      amountFunded: undefined,
      percentComplete: Number.isFinite(percentComplete) ? percentComplete : undefined,
      comment: newRowForm.comment.trim() || undefined,
      isLastMonth: false,
    };
    setTableData(prev => {
      const updated = recalcPercentages(sortRows([...prev, newRow]), revisedBudget);
      updatedRowsRef.current = updated;
      return updated;
    });
    setIsAddDialogOpen(false);
    setNewRowForm(createEmptyForm());
  };

  const columns = useMemo<MRT_ColumnDef<ScheduleGraphRowDTO>[]>(() => {
    return [
      {
        header: 'App #',
        accessorKey: 'applicationNumber',
        size: 80,
        muiTableBodyCellProps: { align: 'left' },
        muiTableHeadCellProps: { align: 'left' },
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant='body2'>{row.original.applicationNumber ?? '—'}</Typography>
            {row.original.isLastMonth && (
              <Tooltip title='Auto-added from previous draw' arrow={false}>
                <HistoryIcon sx={{ fontSize: 13, color: theme.palette.text.secondary, opacity: 0.55 }} />
              </Tooltip>
            )}
          </Box>
        ),
        muiEditTextFieldProps: ({ row }) => ({
          size: 'small' as const,
          variant: 'outlined' as const,
          onBlur: (e: React.FocusEvent<HTMLInputElement>) =>
            updateRow(row.original.id, { applicationNumber: e.target.value.trim() || undefined }),
        }),
      },
      {
        header: 'Application Date',
        accessorKey: 'applicationDate',
        size: 152,
        muiTableBodyCellProps: { align: 'left' },
        muiTableHeadCellProps: { align: 'left' },
        Cell: ({ row }) => (
          <Typography variant='body2' sx={{ color: row.original.isLastMonth ? 'text.secondary' : 'text.primary' }}>
            {row.original.applicationDate
              ? new Date(row.original.applicationDate.split('T')[0] + 'T12:00:00').toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })
              : '—'}
          </Typography>
        ),
        Edit: ({ row, table }) => <DatePickerCell row={row} table={table} updateRow={updateRow} />,
      },
      {
        header: 'Amount Funded',
        accessorKey: 'amountFunded',
        size: 140,
        muiTableBodyCellProps: { align: 'right' },
        muiTableHeadCellProps: { align: 'right' },
        Cell: ({ row }) => (
          <Typography variant='body2' sx={{ color: row.original.isLastMonth ? 'text.secondary' : 'text.primary' }}>
            {row.original.amountFunded != null ? formatCurrency(String(row.original.amountFunded)) : '—'}
          </Typography>
        ),
        muiEditTextFieldProps: ({ row }) => ({
          size: 'small' as const,
          type: 'number',
          variant: 'outlined' as const,
          inputProps: { min: 0, step: 0.01 },
          onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
            const val = parseFloat(e.target.value);
            updateRow(row.original.id, { amountFunded: Number.isFinite(val) ? val : undefined });
          },
        }),
      },
      {
        header: '% Complete',
        accessorKey: 'percentComplete',
        size: 118,
        muiTableBodyCellProps: { align: 'right' },
        muiTableHeadCellProps: { align: 'right' },
        Cell: ({ row }) => (
          <Typography variant='body2' color='text.primary'>
            {row.original.percentComplete != null ? `${row.original.percentComplete.toFixed(2)}%` : '—'}
          </Typography>
        ),
        muiEditTextFieldProps: ({ row }) => ({
          size: 'small' as const,
          type: 'number',
          variant: 'outlined' as const,
          inputProps: { min: 0, max: 100, step: 0.01 },
          onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
            const val = parseFloat(e.target.value);
            // A manually-set % Complete would otherwise be recalculated away on the next edit if
            // the row still carries an amountFunded — clear it so the manual value sticks.
            updateRow(row.original.id, { amountFunded: undefined, percentComplete: Number.isFinite(val) ? val : undefined });
          },
        }),
      },
      {
        header: 'Comment',
        accessorKey: 'comment',
        size: 180,
        muiTableBodyCellProps: { align: 'left' },
        muiTableHeadCellProps: { align: 'left' },
        Cell: ({ row }) => (
          <Typography variant='body2' sx={{ color: row.original.isLastMonth ? 'text.secondary' : 'text.primary' }}>
            {row.original.comment || ''}
          </Typography>
        ),
        muiEditTextFieldProps: ({ row }) => ({
          size: 'small' as const,
          variant: 'outlined' as const,
          onBlur: (e: React.FocusEvent<HTMLInputElement>) => updateRow(row.original.id, { comment: e.target.value.trim() || undefined }),
        }),
      },
    ];
  }, [updateRow, theme]);

  // Sorted in for viewing only — never part of tableData/onChange (see buildCommencementRow above).
  const displayRows = useMemo<ScheduleGraphRowDTO[]>(() => {
    if (!commencementDate) return tableData;
    return sortRows([buildCommencementRow(commencementDate), ...tableData]);
  }, [tableData, commencementDate]);

  const table = useMaterialReactTable<ScheduleGraphRowDTO>({
    columns,
    data: displayRows,
    ...commonMRTStyling,
    layoutMode: 'grid',
    enableEditing: row => !row.original.isCommencement,
    editDisplayMode: 'cell',
    enableColumnActions: false,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enableSorting: false,
    enablePagination: false,
    enableBottomToolbar: false,
    enableDensityToggle: false,
    enableHiding: false,
    enableFullScreenToggle: false,
    renderToolbarInternalActions: () => <></>,
    enableStickyHeader: true,
    enableRowActions: true,
    positionActionsColumn: 'last',
    enableColumnPinning: true,
    // amountFunded column hidden, not removed — still used by the % complete calc and Add Row dialog.
    initialState: { columnPinning: { right: ['mrt-row-actions'] }, columnVisibility: { amountFunded: false } },
    muiTableContainerProps: { sx: { maxHeight: '420px' } },
    muiTableBodyRowProps: ({ row }) => {
      const backgroundColor = row.index % 2 === 0 ? '#f9f9f9' : '#ffffff';
      return {
        onMouseEnter: () => handleRowEnter(row.original.id),
        onMouseMove: () => handleRowEnter(row.original.id),
        onMouseLeave: handleRowLeave,
        sx: {
          opacity: row.original.isLastMonth ? 0.75 : 1,
          cursor: 'pointer',
          backgroundColor,
          '&:hover': { backgroundColor: (theme: any) => theme.palette.action.hover },
          '& td[data-pinned="true"]::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor,
            zIndex: -1,
          },
        },
      };
    },
    renderRowActions: ({ row }) => {
      const deleteDisabled = !!row.original.isLastMonth || !!row.original.isCommencement;
      const deleteTitle = row.original.isCommencement
        ? 'Commencement is derived from the Commencement Date and cannot be deleted here'
        : deleteDisabled
          ? 'Auto-added rows cannot be deleted'
          : 'Delete row';
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Tooltip title={deleteTitle} arrow={false}>
            <span>
              <WhiteFab
                size='small'
                sx={{ width: 32, height: 32, minHeight: 32 }}
                icon={<DeleteIcon fontSize='small' />}
                textColor='error'
                onClick={() => handleDelete(row)}
                disabled={deleteDisabled}
              />
            </span>
          </Tooltip>
        </Box>
      );
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        ...actionsColumnStyling,
        header: '',
        size: 50,
        Header: () => null,
        Footer: () => '',
      },
    },
    muiTopToolbarProps: {
      sx: {
        backgroundColor: 'white !important',
        boxShadow: 'none',
        minHeight: 40,
        borderRadius: 'inherit',
        borderBottom: '1px solid rgba(224, 224, 224, 1)',
      },
    },
    renderTopToolbarCustomActions: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', px: 1, py: 0.5 }}>
        <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
          Schedule from Previous Fundings
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {revisedBudget != null && revisedBudget > 0 && (
            <Typography variant='caption' color='text.secondary'>
              Revised Budget: {formatCurrency(String(revisedBudget))}
            </Typography>
          )}
          {previousFundings != null && (
            <Tooltip title='Repopulate from Previous Fundings' arrow={false}>
              <span>
                <WhiteFab
                  size='small'
                  sx={{ width: 32, height: 32, minHeight: 32 }}
                  icon={<ReplayIcon fontSize='small' />}
                  textColor='primary'
                  onClick={() => setIsRepopulateConfirmOpen(true)}
                />
              </span>
            </Tooltip>
          )}
          <Tooltip title='Add row' arrow={false}>
            <span>
              <WhiteFab
                size='small'
                sx={{ width: 32, height: 32, minHeight: 32 }}
                icon={<AddIcon fontSize='small' />}
                textColor='primary'
                onClick={() => {
                  setNewRowForm(createEmptyForm());
                  setIsAddDialogOpen(true);
                }}
              />
            </span>
          </Tooltip>
        </Box>
      </Box>
    ),
    enableTopToolbar: true,
  });

  return (
    <>
      <Dialog open={isAddDialogOpen} onClose={() => setIsAddDialogOpen(false)} fullWidth maxWidth='xs'>
        <DialogTitle>Add Schedule Graph Row</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label='Application #'
              fullWidth
              size='small'
              value={newRowForm.applicationNumber}
              onChange={e => setNewRowForm(p => ({ ...p, applicationNumber: e.target.value }))}
            />
            <DynamicDatePicker
              fieldName='applicationDate'
              value={newRowForm.applicationDate}
              onChange={(_field, val) => setNewRowForm(p => ({ ...p, applicationDate: val }))}
              label='Application Date'
              textFieldProps={{ size: 'small', placeholder: 'mm/dd/yyyy', fullWidth: true }}
            />
            <TextField
              label='% Complete'
              fullWidth
              size='small'
              type='number'
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              value={newRowForm.percentComplete}
              onChange={e => setNewRowForm(p => ({ ...p, percentComplete: e.target.value }))}
            />
            <TextField
              label='Comment'
              fullWidth
              size='small'
              value={newRowForm.comment}
              onChange={e => setNewRowForm(p => ({ ...p, comment: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddDialogOpen(false)} color='inherit'>
            Cancel
          </Button>
          <Button onClick={handleConfirmAdd} variant='contained' sx={{ color: theme.palette.common.white }}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={isRepopulateConfirmOpen} onClose={() => setIsRepopulateConfirmOpen(false)} fullWidth maxWidth='xs'>
        <DialogTitle>Repopulate from Previous Fundings?</DialogTitle>
        <DialogContent>
          <Typography variant='body2'>
            This replaces every row currently in this table, including any you&apos;ve added or edited by hand, with one row per entry in
            the Previous Fundings table, recalculating % Complete from scratch. This can&apos;t be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsRepopulateConfirmOpen(false)} color='inherit'>
            Cancel
          </Button>
          <Button onClick={handleRepopulateFromPreviousFundings} variant='contained' sx={{ color: theme.palette.common.white }}>
            Repopulate
          </Button>
        </DialogActions>
      </Dialog>
      <MaterialReactTable table={table} />
    </>
  );
};

export default ScheduleGraphTable;
